/**
 * Custom hook for managing Admin Settings state and operations
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { platformSettingsAPI, preferencesAPI, type SiteRegionalSettings, type PreferencesChoices, type GroupRegionalDefaults, type GroupRegionalDefaultsUpdate, type PurchasingTabsVisibility, type QualityTabsVisibility } from '../../../lib/api';
import { useMenuSettings, DEFAULT_MENU_VISIBILITY } from '../../../contexts/MenuSettingsContext';
import { useBranding } from '../../../contexts/BrandingContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import {
  BRANDING_ACCEPT_MAP, BRANDING_FIELD_MAP, brandingLabel, savedImageUrl,
} from './brandingUpload';
import { companyContactDetailsPayload } from './companyContactDetails';
import {
  ThemeSettings,
  PlatformSettings,
  BarrattFlags,
  DEFAULT_THEME_SETTINGS,
  DEFAULT_BARRATT_FLAGS,
  DEFAULT_DASHBOARD_TABS,
  DEFAULT_SUPPLIER_DASHBOARD_TABS,
  DEFAULT_SUPPLIER_CARD_FIELDS,
  DEFAULT_DATA_IMPORT_TILES,
  DashboardTabsVisibility,
  SupplierCardFieldsVisibility,
  DataImportTilesVisibility,
  DEFAULT_WRITEBACK_RULES,
  WritebackRules,
} from './types';

/**
 * Whether a value is a real settings object rather than the click event React
 * passes to a Save button's handler.
 *
 * @param value - The candidate override handed to `savePlatformSettings`.
 * @returns True only for a settings object.
 */
function isPlatformSettings(value: unknown): value is PlatformSettings {
  return typeof value === 'object' && value !== null
    && 'menuVisibility' in value && 'mainMenuType' in value;
}

export function useAdminSettings() {
  const { menuVisibility: contextMenuVisibility, updateMenuVisibility, loadSettingsFromBackend, updateSupplierCardFields, updateDataImportTiles } = useMenuSettings();
  const { loadCompanyBranding, resetColorsOnly } = useBranding();
  const { user } = useAuth();
  // Mirrors the backend's WRITEBACK_SETTING_KEYS guard.
  const canManageWriteback = Boolean(user?.is_superuser || user?.is_staff);

  // Theme settings state
  const [themeSettings, setThemeSettings] = useState<ThemeSettings>(DEFAULT_THEME_SETTINGS);

  // Platform settings state - Initialize with context menu visibility
  const [platformSettings, setPlatformSettings] = useState<PlatformSettings>(() => ({
    mainMenuType: '2',
    freeMode: false,
    mainCompanyCanLoginAs: false,
    defaultLandingSite: null,
    defaultLandingGroup: null,
    automationRelated: false,
    useDispositionInNCR: false,
    warehouseModule: false,
    turnEmailMAB: false,
    switchPartRepository: false,
    qualityMeasurePPK: false,
    writebackEnabled: true,
    writebackShowSapRow: false,
    writebackRulesEffective: DEFAULT_WRITEBACK_RULES,
    enable8dScoring: true,
    deliveryAppealBulkEnabled: false,
    auditLiteEnabled: false,
    notificationIconTop: true,
    notificationIconSide: false,
    ncrFaultClassificationOptional: false,
    restrictCountry: '',
    restrictCountryEnabled: false,
    menuVisibility: contextMenuVisibility,
    ncrTemplate: 1,
    // Upload Limits (Feature 5)
    maxUploadSizeMb: 50,
    allowedFileTypes: [],
    // Document Versioning (Feature 8)
    autoVersionDocuments: true,
    versionFormat: 'numeric',
    retainOldVersions: true,
    // Notification Defaults (Feature 3)
    defaultNotificationPreferences: {},
    systemAlertEmails: '',
    // Supplier Onboarding
    supplierOnboardingEnabled: true,
    organisationStructure: '',
    // Hierarchy Labels
    hierarchyLevel1Label: 'Group',
    hierarchyLevel2Label: 'Division',
    hierarchyLevel3Label: 'Company',
    supplierDefaultLandingPath: '/dashboard',
    poLineSchedules: false,
  }));

  // File upload states
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: boolean }>({});
  // CR-6243: the stored URL of each branding image, keyed by the tab's own
  // fileType key, so the upload tiles can show what is currently applied
  // instead of a permanent empty placeholder.
  const [brandingImages, setBrandingImages] = useState<Record<string, string | null>>({});
  const [saving, setSaving] = useState(false);

  // Company regional settings state
  const [siteSettings, setSiteSettings] = useState<SiteRegionalSettings[]>([]);
  const [choices, setChoices] = useState<PreferencesChoices | null>(null);
  const [loadingSites, setLoadingSites] = useState(false);
  const [savingSite, setSavingSite] = useState<number | null>(null);
  const [editedSites, setEditedSites] = useState<{ [key: number]: Partial<SiteRegionalSettings> }>({});
  // CR-6256: group defaults shown at the top of Regional Settings, and the
  // site currently switching between inherit and override.
  const [groupDefaults, setGroupDefaults] = useState<GroupRegionalDefaults | null>(null);
  const [loadingGroupDefaults, setLoadingGroupDefaults] = useState(false);
  const [togglingInheritance, setTogglingInheritance] = useState<number | null>(null);
  const [savingGroupDefaults, setSavingGroupDefaults] = useState(false);
  const [siteSettingsError, setSiteSettingsError] = useState<string | null>(null);

  // Barratt feature flags state
  const [barrattFlags, setBarrattFlags] = useState<BarrattFlags>(DEFAULT_BARRATT_FLAGS);
  // CR-6160: the raw feature_flags dict as loaded from the backend. A platform
  // settings save rebuilds feature_flags from the managed subset (barrattFlags +
  // po_line_schedules); spreading this first preserves any sibling flag not in
  // that subset (e.g. asn_produmax_export, schedule_collaboration) instead of
  // silently dropping it.
  const loadedFeatureFlagsRef = useRef<Record<string, unknown>>({});
  // Customer review 11 Sep (item 35): true once the platform settings have
  // been applied. Until then every section renders DEFAULT_* values, and a Save
  // would write those defaults over the tenant's real settings (e.g. switch the
  // Forecast Total row off) — savePlatformSettings refuses until loaded.
  const platformLoadedRef = useRef(false);

  // Dashboard tab visibility state (CR-4441)
  const [dashboardTabs, setDashboardTabs] = useState<DashboardTabsVisibility>(DEFAULT_DASHBOARD_TABS);
  const [supplierDashboardTabs, setSupplierDashboardTabs] = useState<DashboardTabsVisibility>(DEFAULT_SUPPLIER_DASHBOARD_TABS);
  // CR-9099: the SAVED platform tabs; the menu context holds the viewed site's.
  const [savedDashboardTabs, setSavedDashboardTabs] = useState<DashboardTabsVisibility>(DEFAULT_DASHBOARD_TABS);

  // Purchasing & Quality tab visibility
  const [purchasingTabs, setPurchasingTabs] = useState<PurchasingTabsVisibility>({
    rfq: true, pricing: true, po_headers: true, po_lines: true,
    forecast: true, asn: true, deliveries: true, call_off: true, late_to_raise: false,
  });
  const [qualityTabs, setQualityTabs] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });

  // Supplier card field visibility
  const [supplierCardFields, setSupplierCardFields] = useState<SupplierCardFieldsVisibility>(DEFAULT_SUPPLIER_CARD_FIELDS);

  // Data Import tile visibility
  const [dataImportTiles, setDataImportTiles] = useState<DataImportTilesVisibility>(DEFAULT_DATA_IMPORT_TILES);

  // Sync platform settings menu visibility with context when context changes
  useEffect(() => {
    setPlatformSettings(prev => ({
      ...prev,
      menuVisibility: contextMenuVisibility,
    }));
  }, [contextMenuVisibility]);

  // Load settings from API
  useEffect(() => {
    loadSettings();
    loadSiteSettings();
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      // CR-6150: company branding is company-scoped and loads for any admin,
      // but the platform-settings call (GET /core/settings/platform/) is
      // Helix-only and 403s for a customer/group admin. Use allSettled so that
      // 403 doesn't reject the whole load and blank the Branding screen — the
      // platform-only fields just fall back to theme defaults.
      const [platformResult, brandingResult] = await Promise.allSettled([
        platformSettingsAPI.getSettings(),
        platformSettingsAPI.getCompanyBranding(),
      ]);

      // Branding itself failing is the real error worth surfacing.
      if (brandingResult.status === 'rejected') {
        throw brandingResult.reason;
      }
      const branding = brandingResult.value.data;

      // PR #635 review: only swallow the EXPECTED 403 from the Helix-only
      // platform-settings endpoint (a customer/group admin still loads their
      // Branding screen from company-branding). Any other platform-settings
      // failure (500, network, timeout) is a real outage — re-throw it so the
      // catch/toast below surfaces it instead of silently proceeding on
      // defaults and hiding the problem from a Helix admin.
      if (platformResult.status === 'rejected') {
        const platformErrorStatus = (
          platformResult.reason as { response?: { status?: number } }
        )?.response?.status;
        if (platformErrorStatus !== 403) {
          throw platformResult.reason;
        }
      }
      const settings = platformResult.status === 'fulfilled'
        ? platformResult.value.data
        : null;

      // Default colors for fallback
      const defaultColors = {
        backgroundColor: '#ffffff',
        loginColor: '#4f46e5',
        fontColor: '#333333',
        buttonColor: '#4f46e5',
      };

      // Update theme settings from company branding (per-company isolation).
      // Company branding always applies; the platform-only fields (themes,
      // emails, website) only overlay when the Helix-only settings loaded.
      setThemeSettings({
        ...DEFAULT_THEME_SETTINGS,
        companyName: branding.company_name || settings?.company_name || '',
        loginTitle: branding.login_title || '',
        // Use company branding colors, fall back to defaults (not platform settings)
        backgroundColor: branding.background_color || defaultColors.backgroundColor,
        loginColor: branding.login_color || defaultColors.loginColor,
        fontColor: branding.font_color || defaultColors.fontColor,
        buttonColor: branding.button_color || defaultColors.buttonColor,
        ...(settings ? {
          defaultTheme: settings.default_theme,
          availableThemes: settings.available_themes,
          website: settings.website,
          supportEmail: settings.support_email,
          salesEmail: settings.sales_email,
          noReplyEmail: settings.no_reply_email,
          contactEmail: (settings as unknown as Record<string, string>)['contact_email'] || '',
          companyAddress: (settings as unknown as Record<string, string>)['company_address'] || '',
          companyPhone: (settings as unknown as Record<string, string>)['company_phone'] || '',
          emailSubject: settings.email_subject,
          emailSignature: settings.email_signature,
        } : {}),
      });

      // Platform-level settings (menus, feature flags, tab visibility) are
      // Helix-only — skip applying them entirely when they didn't load for a
      // customer/group admin (their Branding screen has already populated).
      if (!settings) {
        return;
      }

      // Update platform settings
      setPlatformSettings({
        mainMenuType: settings.main_menu_type,
        freeMode: settings.free_mode,
        mainCompanyCanLoginAs: settings.main_company_can_login_as,
        // CR-5371: id of the Company users land on after login when no
        // current_site is set. null = follow existing default behaviour.
        defaultLandingSite: (settings as unknown as Record<string, number | null>)['default_landing_site'] ?? null,
        // CR-5371 rework: id of the CompanyGroup alternative — mutually
        // exclusive with defaultLandingSite.
        defaultLandingGroup: (settings as unknown as Record<string, number | null>)['default_landing_group'] ?? null,
        automationRelated: settings.automation_related,
        useDispositionInNCR: settings.use_disposition_in_ncr,
        warehouseModule: settings.warehouse_module,
        turnEmailMAB: settings.turn_email_mab,
        switchPartRepository: settings.switch_part_repository,
        qualityMeasurePPK: settings.quality_measure_ppk,
        writebackEnabled: (settings as unknown as Record<string, boolean>)['writeback_enabled'] ?? true,
        writebackShowSapRow: (settings as unknown as Record<string, boolean>)['writeback_show_sap_row'] ?? false,
        // Backend merges its shipped defaults into writeback_rules_effective;
        // fall back to the local mirror only if the field is missing entirely.
        writebackRulesEffective: {
          ...DEFAULT_WRITEBACK_RULES,
          ...((settings as unknown as Record<string, WritebackRules | undefined>)['writeback_rules_effective'] ?? {}),
        },
        enable8dScoring: settings.enable_8d_scoring ?? true,
        deliveryAppealBulkEnabled: (settings as unknown as Record<string, boolean>)['delivery_appeal_bulk_enabled'] ?? false,
        auditLiteEnabled: (settings as unknown as Record<string, boolean>)['audit_lite_enabled'] ?? false,
        notificationIconTop: (settings as unknown as Record<string, boolean>)['notification_icon_top'] ?? true,
        notificationIconSide: (settings as unknown as Record<string, boolean>)['notification_icon_side'] ?? false,
        ncrFaultClassificationOptional: (settings as unknown as Record<string, boolean>)['ncr_fault_classification_optional'] ?? false,
        restrictCountry: settings.restrict_country,
        restrictCountryEnabled: settings.restrict_country_enabled,
        // Merge defaults so admin toggles reflect the intended default state
        // even when the backend's stored dict is missing newer keys
        // (e.g. partsMaster). Without this merge, a missing key renders the
        // Switch as unchecked and any save will omit that key, so the
        // sidebar silently falls back to its own default and stays visible.
        menuVisibility: { ...DEFAULT_MENU_VISIBILITY, ...settings.menu_visibility },
        ncrTemplate: ((settings as unknown as Record<string, number>)['ncr_template'] || 1) as 1 | 2,
        // Upload Limits (Feature 5)
        maxUploadSizeMb: settings.max_upload_size_mb ?? 50,
        allowedFileTypes: settings.allowed_file_types ?? [],
        // Document Versioning (Feature 8)
        autoVersionDocuments: settings.auto_version_documents ?? true,
        versionFormat: settings.version_format ?? 'numeric',
        retainOldVersions: settings.retain_old_versions ?? true,
        // Notification Defaults (Feature 3)
        defaultNotificationPreferences: settings.default_notification_preferences ?? {},
        systemAlertEmails: settings.system_alert_emails ?? '',
        // Supplier Onboarding
        supplierOnboardingEnabled: settings.supplier_onboarding_enabled ?? true,
        organisationStructure: settings.organisation_structure ?? '',
        // Hierarchy Labels
        hierarchyLevel1Label: settings.hierarchy_level_1_label ?? 'Group',
        hierarchyLevel2Label: settings.hierarchy_level_2_label ?? 'Division',
        hierarchyLevel3Label: settings.hierarchy_level_3_label ?? 'Company',
        supplierDefaultLandingPath: settings.supplier_default_landing_path ?? '/dashboard',
        poLineSchedules: settings.feature_flags?.po_line_schedules ?? false,
      });

      // CR-6243: keep the saved branding image URLs so each upload tile can
      // preview what is actually applied. Keyed by the tab's fileType, which
      // is what onFileUpload already speaks.
      setBrandingImages({
        companyLogo: settings.company_logo ?? null,
        companyDarkLogo: settings.company_dark_logo ?? null,
        headerLogo: settings.header_logo ?? null,
        loginImage: settings.login_image ?? null,
      });

      // Also update context with loaded menu visibility (defaults merged)
      updateMenuVisibility({ ...DEFAULT_MENU_VISIBILITY, ...settings.menu_visibility });

      // Load dashboard tab visibility (CR-4441)
      if (settings.dashboard_tabs) {
        const loadedTabs = { ...DEFAULT_DASHBOARD_TABS, ...settings.dashboard_tabs };
        setDashboardTabs(loadedTabs);
        setSavedDashboardTabs(loadedTabs);
      }
      if (settings.supplier_dashboard_tabs) {
        setSupplierDashboardTabs({
          ...DEFAULT_SUPPLIER_DASHBOARD_TABS,
          ...settings.supplier_dashboard_tabs,
        });
      }
      // Load purchasing & quality tab visibility
      if (settings.purchasing_tabs) {
        setPurchasingTabs((prev: PurchasingTabsVisibility) => ({ ...prev, ...settings.purchasing_tabs }));
      }
      if (settings.quality_tabs) {
        setQualityTabs((prev: QualityTabsVisibility) => ({ ...prev, ...settings.quality_tabs }));
      }
      // Load supplier card field visibility
      if (settings.supplier_card_fields) {
        setSupplierCardFields({
          ...DEFAULT_SUPPLIER_CARD_FIELDS,
          ...settings.supplier_card_fields,
        });
      }
      // Load Data Import tile visibility
      if (settings.data_import_tiles) {
        setDataImportTiles({
          ...DEFAULT_DATA_IMPORT_TILES,
          ...settings.data_import_tiles,
        });
      }

      // Load Barratt feature flags
      if (settings.feature_flags) {
        // CR-6160: keep the full dict so unmanaged sibling flags survive a save.
        loadedFeatureFlagsRef.current = settings.feature_flags as Record<string, unknown>;
        platformLoadedRef.current = true;
        setBarrattFlags({
          delivery_appeals: settings.feature_flags.delivery_appeals ?? false,
          spend_leakage: settings.feature_flags.spend_leakage ?? false,
          bulk_upload: settings.feature_flags.bulk_upload ?? false,
          commodity_codes: settings.feature_flags.commodity_codes ?? false,
          // CR-6726: allow_supplier_decline is no longer read into the managed
          // subset — Admin Setup owns it. loadedFeatureFlagsRef still carries
          // it, so a Feature Settings save preserves whatever the customer set
          // instead of dropping it.
          show_forecast_total: settings.feature_flags.show_forecast_total ?? true,
          // CR-6160: paid ASN logistics-tracking module (default OFF).
          asn_logistics_tracking: settings.feature_flags.asn_logistics_tracking ?? false,
          // CR-6274: GRN Repair module. Absent = ON — an install that predates
          // the toggle already has the toolbox and must keep it.
          grn_repair: settings.feature_flags.grn_repair ?? true,
          forecast_weeks_only: settings.feature_flags.forecast_weeks_only ?? false,
          forecast_hide_supplier_part_no: settings.feature_flags.forecast_hide_supplier_part_no ?? false,
          forecast_description_from_master: settings.feature_flags.forecast_description_from_master ?? false,
          // Unset → the install's delivery grain, the server's default too.
          forecast_show_po_number: settings.feature_flags.forecast_show_po_number
            ?? (settings.feature_flags.po_line_schedules ?? false),
          // CR-5334: normalise the legacy 'barratt' value to 'spend_analytics'
          // on read so the selector shows the renamed option even before the
          // data migration has run for this tenant.
          portal_mode: (settings.feature_flags.portal_mode === 'barratt'
            ? 'spend_analytics'
            : settings.feature_flags.portal_mode) ?? 'standard',
        });
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    }
  }, [updateMenuVisibility]);

  const loadSiteSettings = useCallback(async () => {
    setLoadingSites(true);
    setLoadingGroupDefaults(true);
    setSiteSettingsError(null);
    try {
      // CR-6256: the group defaults come from the same scope as the site list,
      // so they are fetched together and always describe the sites shown.
      const [sitesRes, choicesRes, groupDefaultsRes] = await Promise.all([
        preferencesAPI.getSiteRegionalSettings(),
        preferencesAPI.getPreferencesChoices(),
        preferencesAPI.getGroupRegionalDefaults(),
      ]);
      setSiteSettings(Array.isArray(sitesRes.data) ? sitesRes.data : []);
      setChoices(choicesRes.data);
      setGroupDefaults(groupDefaultsRes.data);
    } catch (error) {
      console.error('Failed to load site settings:', error);
      // A toast alone fades and leaves the page showing "no group defaults" /
      // "no companies", which reads as configuration rather than failure.
      setSiteSettingsError('Could not load regional settings. Check your connection and try again.');
      toast.error('Failed to load company regional settings');
    } finally {
      setLoadingSites(false);
      setLoadingGroupDefaults(false);
    }
  }, []);

  const handleSiteSettingChange = useCallback((siteId: number, field: string, value: string | boolean | null) => {
    setEditedSites(prev => ({
      ...prev,
      [siteId]: {
        ...prev[siteId],
        [field]: value === '' ? null : value,
      },
    }));
  }, []);

  /**
   * CR-6256: set the group's own regional defaults.
   *
   * A null field clears the group value so it falls back to the parent group or
   * the platform default. Reloads afterwards because every inheriting site's
   * displayed values move with this.
   */
  const saveGroupDefaults = useCallback(async (values: GroupRegionalDefaultsUpdate) => {
    setSavingGroupDefaults(true);
    try {
      const res = await preferencesAPI.updateGroupRegionalDefaults(values);
      setGroupDefaults(res.data);
      toast.success('Group default preferences saved');
      loadSiteSettings();
    } catch (error) {
      console.error('Failed to save group defaults:', error);
      toast.error('Failed to save group default preferences');
    } finally {
      setSavingGroupDefaults(false);
    }
  }, [loadSiteSettings]);

  /**
   * CR-6256: switch a site between inheriting the group defaults and holding
   * its own. Persisted on click rather than behind the Save button — it is a
   * single deliberate choice, and leaving it unsaved would show a site as
   * inheriting when it still overrides.
   *
   * The response replaces the row rather than just flipping the flag locally:
   * moving to Override seeds the site's own columns with the values it was
   * inheriting, and the dropdowns that appear must open on those, not on
   * whatever the row was last configured with.
   *
   * Any pending dropdown edits for the site are discarded, since they describe
   * a state the user has just left.
   */
  const setSiteInheritance = useCallback(async (siteId: number, usesGroupDefaults: boolean) => {
    setTogglingInheritance(siteId);
    try {
      const res = await preferencesAPI.updateSiteRegionalSettings(siteId, {
        uses_group_regional_defaults: usesGroupDefaults,
      });
      setEditedSites(prev => {
        const { [siteId]: _discarded, ...rest } = prev;
        return rest;
      });
      setSiteSettings(prev => prev.map(site => (
        site.id === siteId
          ? { ...site, ...res.data, uses_group_regional_defaults: usesGroupDefaults }
          : site
      )));
      toast.success(
        usesGroupDefaults
          ? 'Site now inherits the group company defaults'
          : 'Site now overrides the group company defaults',
      );
    } catch (error) {
      console.error('Failed to update site inheritance:', error);
      toast.error('Failed to update site regional settings');
    } finally {
      setTogglingInheritance(null);
    }
  }, []);

  const saveSiteSettings = useCallback(async (siteId: number) => {
    const edits = editedSites[siteId];
    if (!edits) return;

    setSavingSite(siteId);
    try {
      await preferencesAPI.updateSiteRegionalSettings(siteId, edits);
      toast.success('Company regional settings saved successfully!');
      // Clear edits for this site
      setEditedSites(prev => {
        const { [siteId]: _, ...rest } = prev;
        return rest;
      });
      // Reload site settings
      loadSiteSettings();
    } catch (error) {
      console.error('Failed to save site settings:', error);
      toast.error('Failed to save company regional settings');
    } finally {
      setSavingSite(null);
    }
  }, [editedSites, loadSiteSettings]);

  const saveThemeSettings = useCallback(async () => {
    // Platform Name is required for display purposes.
    if (!themeSettings.companyName.trim()) {
      toast.error('Platform Name is required', {
        description: 'Enter a value in Main Company Details before saving.',
        duration: 4000,
      });
      return;
    }

    setSaving(true);
    try {
      // CR-8681: contact details live with the platform settings they are read
      // from. Sent first, so a refused email stops the save; and only once those
      // settings have loaded, or the blanks would overwrite the stored values.
      if (platformLoadedRef.current) {
        await platformSettingsAPI.patchSettings(companyContactDetailsPayload(themeSettings));
      }
      // Save branding colors to company-specific settings (per-tenant isolation)
      const brandingPayload = {
        company_name: themeSettings.companyName.trim(),
        login_title: themeSettings.loginTitle,
        background_color: themeSettings.backgroundColor,
        login_color: themeSettings.loginColor,
        font_color: themeSettings.fontColor,
        button_color: themeSettings.buttonColor,
      };

      await platformSettingsAPI.patchCompanyBranding(brandingPayload);

      // Reload company branding to apply new colors across the platform
      await loadCompanyBranding();

      toast.success('Branding settings saved successfully!', {
        description: 'Your company branding has been updated.',
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to save branding settings:', error);
      toast.error('Failed to save branding settings', {
        // A refused colour comes back as a field error; say which, not "try again".
        description: getApiErrorMessage(error, 'Please try again or contact support.'),
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  }, [themeSettings, loadCompanyBranding]);

  /**
   * Persist the platform settings.
   *
   * @param override - The settings to save, for a caller that has just built a
   *   new object and has not re-rendered yet. A toggle that saves itself on
   *   change (Onboarding) must pass this: `setPlatformSettings` only schedules
   *   the re-render, so this callback's closure still holds the pre-toggle
   *   value and would otherwise PATCH the state the user just changed away
   *   from — then `loadSettingsFromBackend` below would pull it back and the
   *   switch would visibly snap back. Omit it from a Save button, which runs a
   *   render later and whose closure is already current.
   */
  const savePlatformSettings = useCallback(async (override?: PlatformSettings) => {
    if (!platformLoadedRef.current) {
      toast.error('Settings are still loading — try again in a moment.');
      return;
    }
    // Every Save button is wired `onClick={onSave}` behind an `onSave: () => void`
    // prop, so React hands this a MouseEvent at runtime and the prop's type
    // erases the parameter rather than rejecting it. Taking that as the override
    // would PATCH a payload of undefined, so only a real settings object counts.
    const settings = isPlatformSettings(override) ? override : platformSettings;
    setSaving(true);
    try {
      const payload = {
        main_menu_type: settings.mainMenuType,
        free_mode: settings.freeMode,
        main_company_can_login_as: settings.mainCompanyCanLoginAs,
        // CR-5371 (site/group are mutually exclusive; both sent explicitly
        // so picking one on the frontend clears the other server-side)
        default_landing_site: settings.defaultLandingSite,
        default_landing_group: settings.defaultLandingGroup,
        automation_related: settings.automationRelated,
        use_disposition_in_ncr: settings.useDispositionInNCR,
        warehouse_module: settings.warehouseModule,
        turn_email_mab: settings.turnEmailMAB,
        switch_part_repository: settings.switchPartRepository,
        quality_measure_ppk: settings.qualityMeasurePPK,
        // Writeback keys are platform-staff-only and the backend 403s the whole
        // request if anyone else sends them. This one payload is shared by every
        // Platform Admin tab, so including them unconditionally would block a
        // non-staff Helix admin from saving Features, Platform Config, anything.
        ...(canManageWriteback
          ? {
              writeback_enabled: settings.writebackEnabled,
              writeback_show_sap_row: settings.writebackShowSapRow,
              // Sent whole; the backend validates the keys and stores only the
              // rules that differ from its defaults.
              writeback_rules: settings.writebackRulesEffective,
            }
          : {}),
        enable_8d_scoring: settings.enable8dScoring,
        delivery_appeal_bulk_enabled: settings.deliveryAppealBulkEnabled,
        audit_lite_enabled: settings.auditLiteEnabled,
        notification_icon_top: settings.notificationIconTop,
        notification_icon_side: settings.notificationIconSide,
        ncr_fault_classification_optional: settings.ncrFaultClassificationOptional,
        restrict_country: settings.restrictCountry,
        restrict_country_enabled: settings.restrictCountryEnabled,
        menu_visibility: settings.menuVisibility,
        ncr_template: settings.ncrTemplate,
        // CR-6160: spread the originally-loaded flags first so unmanaged siblings
        // (asn_produmax_export, schedule_collaboration, …) aren't dropped; the
        // managed subset then overlays the current values.
        feature_flags: { ...loadedFeatureFlagsRef.current, ...barrattFlags, po_line_schedules: settings.poLineSchedules },
        // Upload Limits (Feature 5)
        max_upload_size_mb: settings.maxUploadSizeMb,
        allowed_file_types: settings.allowedFileTypes,
        // Document Versioning (Feature 8)
        auto_version_documents: settings.autoVersionDocuments,
        version_format: settings.versionFormat,
        retain_old_versions: settings.retainOldVersions,
        // Notification Defaults (Feature 3)
        default_notification_preferences: settings.defaultNotificationPreferences,
        system_alert_emails: settings.systemAlertEmails,
        // Supplier Onboarding
        supplier_onboarding_enabled: settings.supplierOnboardingEnabled,
        // CR-8725: dashboard_tabs is not sent from here. Only the Dashboard
        // items in Navigation Menu Management save it (saveDashboardTabs), so
        // a Features or Admin Setup save can no longer overwrite it with a
        // stale copy.
        // CR-8227: the hierarchy level names are not sent from here. The
        // Organisation section saves them on their own; sending them with
        // every save stored a copy on a company admin's own company.
        supplier_default_landing_path: settings.supplierDefaultLandingPath,
      };

      await platformSettingsAPI.patchSettings(payload);

      // Also update context with new menu visibility
      updateMenuVisibility(settings.menuVisibility);

      // Refresh all settings from backend
      await loadSettingsFromBackend();

      // Count enabled menu items
      const enabledCount = Object.values(settings.menuVisibility).filter(v => v).length;

      toast.success('Platform settings saved successfully!', {
        description: `Menu visibility updated. ${enabledCount} menu items are now visible.`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save platform settings', {
        description: 'Please try again or contact support.',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  }, [platformSettings, barrattFlags, updateMenuVisibility, loadSettingsFromBackend, canManageWriteback]);

  // CR-4441: Dedicated save for dashboard tab visibility
  const saveDashboardTabs = useCallback(async () => {
    // The backend stores the JSON whole: saved before the load lands, the
    // defaults would replace the instance's real tabs (CR-8725).
    if (!platformLoadedRef.current) {
      toast.error('Settings are still loading — try again in a moment.');
      return;
    }
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({
        dashboard_tabs: dashboardTabs,
        supplier_dashboard_tabs: supplierDashboardTabs,
      });
      setSavedDashboardTabs(dashboardTabs);

      // CR-9099: reload rather than push these global values into the menu
      // context, which holds the viewed site's overrides on top of them.
      await loadSettingsFromBackend();

      toast.success('Dashboard settings saved successfully!');
    } catch (error) {
      console.error('Failed to save dashboard settings:', error);
      toast.error('Failed to save dashboard settings');
    } finally {
      setSaving(false);
    }
  }, [dashboardTabs, supplierDashboardTabs, loadSettingsFromBackend]);

  const savePurchasingTabs = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ purchasing_tabs: purchasingTabs });
      const enabledCount = Object.values(purchasingTabs).filter(v => v).length;
      toast.success('Purchasing settings saved!', {
        description: `${enabledCount} purchasing tabs enabled.`,
      });
    } catch { toast.error('Failed to save purchasing settings'); }
    finally { setSaving(false); }
  }, [purchasingTabs]);

  const saveQualityTabs = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ quality_tabs: qualityTabs });
      const enabledCount = Object.values(qualityTabs).filter(v => v).length;
      toast.success('Quality settings saved!', {
        description: `${enabledCount} quality tabs enabled.`,
      });
    } catch { toast.error('Failed to save quality settings'); }
    finally { setSaving(false); }
  }, [qualityTabs]);

  const saveSupplierCardFields = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ supplier_card_fields: supplierCardFields });
      updateSupplierCardFields(supplierCardFields);
      const enabledCount = Object.values(supplierCardFields).filter(v => v).length;
      toast.success('Supplier card settings saved!', {
        description: `${enabledCount} card elements enabled.`,
      });
    } catch { toast.error('Failed to save supplier card settings'); }
    finally { setSaving(false); }
  }, [supplierCardFields, updateSupplierCardFields]);

  const saveDataImportTiles = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ data_import_tiles: dataImportTiles });
      updateDataImportTiles(dataImportTiles);
      const enabledCount = Object.values(dataImportTiles).filter(v => v).length;
      toast.success('Data Import tiles saved!', {
        description: `${enabledCount} tiles enabled.`,
      });
    } catch { toast.error('Failed to save Data Import tiles'); }
    finally { setSaving(false); }
  }, [dataImportTiles, updateDataImportTiles]);

  const handleFileUpload = useCallback((fileType: string) => {
    const label = brandingLabel(fileType);
    const field = BRANDING_FIELD_MAP[fileType] || fileType;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = BRANDING_ACCEPT_MAP[fileType] || 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      setUploadingFiles(prev => ({ ...prev, [fileType]: true }));
      try {
        const formData = new FormData();
        formData.append(field, file);

        // CR-6243: uploadBrandingImage, NOT patchSettings — the latter routes a
        // FormData through the JSON default Content-Type, which axios turns
        // into `{"company_logo": {}}`. See platformSettings.ts for the detail.
        const res = await platformSettingsAPI.uploadBrandingImage(formData);

        // CR-6243 (AC 4): a 200 is not proof the image was stored — see
        // savedImageUrl. Never claim success without the URL coming back.
        if (!savedImageUrl(res.data, field)) {
          toast.error(`${label} was not saved`, {
            description: 'The server accepted the request but returned no image. Nothing has changed.',
          });
          return;
        }

        toast.success(`${label} uploaded`);
        loadSettings();
      } catch (error) {
        console.error(`Failed to upload ${fileType}:`, error);
        toast.error(`Failed to upload ${label}`, {
          description: getApiErrorMessage(error, 'The image could not be saved. Check the file type and size, then try again.'),
        });
      } finally {
        setUploadingFiles(prev => ({ ...prev, [fileType]: false }));
      }
    };
    input.click();
  }, [loadSettings]);

  const resetColorsToDefault = useCallback(async () => {
    // Hardcoded default colors - these are the true defaults that should be used
    const defaultColors = {
      backgroundColor: '#ffffff',
      loginColor: '#4f46e5',
      fontColor: '#333333',
      buttonColor: '#4f46e5',
    };
    setThemeSettings(prev => ({
      ...prev,
      ...defaultColors,
    }));
    setSaving(true);
    try {
      // Save default colors to company branding (ensures they're stored, not inherited from another company)
      await platformSettingsAPI.patchCompanyBranding({
        background_color: defaultColors.backgroundColor,
        login_color: defaultColors.loginColor,
        font_color: defaultColors.fontColor,
        button_color: defaultColors.buttonColor,
      });

      // Apply default colors to CSS immediately (preserves company name)
      resetColorsOnly();

      toast.success('Colors reset to defaults!');
    } catch (error) {
      console.error('Failed to reset colors:', error);
      toast.error('Failed to reset colors');
    } finally {
      setSaving(false);
    }
  }, [resetColorsOnly]);

  return {
    // Theme state
    themeSettings,
    setThemeSettings,

    // Platform state
    platformSettings,
    setPlatformSettings,

    // Barratt flags
    barrattFlags,
    setBarrattFlags,

    // Dashboard tab visibility (CR-4441)
    dashboardTabs,
    setDashboardTabs,
    savedDashboardTabs,
    supplierDashboardTabs,
    setSupplierDashboardTabs,
    purchasingTabs,
    setPurchasingTabs,
    savePurchasingTabs,
    qualityTabs,
    setQualityTabs,
    saveQualityTabs,
    supplierCardFields,
    setSupplierCardFields,
    saveSupplierCardFields,
    dataImportTiles,
    setDataImportTiles,
    saveDataImportTiles,

    // Site settings state
    siteSettings,
    choices,
    loadingSites,
    savingSite,
    editedSites,
    // CR-6256: group defaults + inherit/override toggle
    groupDefaults,
    loadingGroupDefaults,
    togglingInheritance,
    siteSettingsError,
    savingGroupDefaults,

    // File upload
    uploadingFiles,
    brandingImages,
    saving,

    // Actions
    saveThemeSettings,
    savePlatformSettings,
    saveDashboardTabs,
    handleFileUpload,
    resetColorsToDefault,
    handleSiteSettingChange,
    saveSiteSettings,
    setSiteInheritance,
    saveGroupDefaults,
    loadSiteSettings,
  };
}
